export interface AppTheme {
  id: string;
  name: string;
  emoji: string;
  description: string;
  isLight?: boolean;
  showcaseClass?: string;
  vars: Record<string, string>;
}

export const THEMES: AppTheme[] = [
  /* ─── 1. Neon Noir ─── */
  {
    id: 'neon-noir',
    name: 'Neon Noir',
    emoji: '🟢',
    description: 'Cyberpunk · Matrix vibes',
    showcaseClass: 'ts-theme-neon',
    vars: {
      '--bg-primary':        '#0A0A0A',
      '--bg-secondary':      '#0F1A0F',
      '--bg-card':           '#111811',
      '--bg-card-hover':     '#1A241A',
      '--accent-amber':      '#39FF14',
      '--accent-amber-dark': '#2AD610',
      '--accent-green':      '#FF10F0',
      '--accent-red':        '#FF4500',
      '--border':            '#1E3A1E',
      '--border-subtle':     '#0F1F0F',
      '--text-primary':      '#E8FFE8',
      '--text-secondary':    '#7AAB7A',
      '--text-muted':        '#3D5C3D',
      '--ts-a':              '#39FF14',
      '--ts-b':              '#FF10F0',
      '--ts-gradient':       'linear-gradient(135deg,#39FF14 0%,#FF10F0 100%)',
      '--ts-glow':           'rgba(57,255,20,0.55)',
      '--ts-glass-bg':       'rgba(57,255,20,0.05)',
      '--ts-glass-border':   'rgba(57,255,20,0.18)',
      '--ts-btn-text':       '#000',
    },
  },

  /* ─── 2. Ocean Depth ─── */
  {
    id: 'ocean-depth',
    name: 'Ocean Depth',
    emoji: '🌊',
    description: 'Deep sea · Premium blue',
    showcaseClass: 'ts-theme-ocean',
    vars: {
      '--bg-primary':        '#020818',
      '--bg-secondary':      '#061228',
      '--bg-card':           '#091F3D',
      '--bg-card-hover':     '#102848',
      '--accent-amber':      '#0EA5E9',
      '--accent-amber-dark': '#0284C7',
      '--accent-green':      '#38BDF8',
      '--accent-red':        '#F472B6',
      '--border':            '#1E3F6B',
      '--border-subtle':     '#0F2440',
      '--text-primary':      '#E0F2FE',
      '--text-secondary':    '#7CB8D9',
      '--text-muted':        '#3A6080',
      '--ts-a':              '#0EA5E9',
      '--ts-b':              '#7DD3FC',
      '--ts-gradient':       'linear-gradient(135deg,#0EA5E9 0%,#7DD3FC 100%)',
      '--ts-glow':           'rgba(14,165,233,0.55)',
      '--ts-glass-bg':       'rgba(14,165,233,0.07)',
      '--ts-glass-border':   'rgba(14,165,233,0.2)',
      '--ts-btn-text':       '#000',
    },
  },

  /* ─── 3. Sunset FC ─── */
  {
    id: 'sunset-fc',
    name: 'Sunset FC',
    emoji: '🌅',
    description: 'Orange × Pink · Instagram era',
    showcaseClass: 'ts-theme-sunset',
    vars: {
      '--bg-primary':        '#0F0A14',
      '--bg-secondary':      '#1A0F1E',
      '--bg-card':           '#211428',
      '--bg-card-hover':     '#2A1B30',
      '--accent-amber':      '#F97316',
      '--accent-amber-dark': '#EA6B0E',
      '--accent-green':      '#EC4899',
      '--accent-red':        '#FB7185',
      '--border':            '#3D1F4A',
      '--border-subtle':     '#261330',
      '--text-primary':      '#FFF0F8',
      '--text-secondary':    '#C49AAF',
      '--text-muted':        '#6B3D5A',
      '--ts-a':              '#F97316',
      '--ts-b':              '#EC4899',
      '--ts-gradient':       'linear-gradient(135deg,#F97316 0%,#EC4899 100%)',
      '--ts-glow':           'rgba(249,115,22,0.5)',
      '--ts-glass-bg':       'rgba(249,115,22,0.06)',
      '--ts-glass-border':   'rgba(249,115,22,0.16)',
      '--ts-btn-text':       '#fff',
    },
  },

  /* ─── 4. Arctic (light) ─── */
  {
    id: 'arctic',
    name: 'Arctic',
    emoji: '🧊',
    description: 'Light · Notion meets sport',
    isLight: true,
    showcaseClass: 'ts-theme-arctic',
    vars: {
      '--bg-primary':        '#F8FAFC',
      '--bg-secondary':      '#F1F5F9',
      '--bg-card':           '#FFFFFF',
      '--bg-card-hover':     '#F8FAFC',
      '--accent-amber':      '#6366F1',
      '--accent-amber-dark': '#4F46E5',
      '--accent-green':      '#818CF8',
      '--accent-red':        '#F43F5E',
      '--border':            '#CBD5E1',
      '--border-subtle':     '#E2E8F0',
      '--text-primary':      '#0F172A',
      '--text-secondary':    '#475569',
      '--text-muted':        '#94A3B8',
      '--ts-a':              '#6366F1',
      '--ts-b':              '#818CF8',
      '--ts-gradient':       'linear-gradient(135deg,#6366F1 0%,#818CF8 100%)',
      '--ts-glow':           'rgba(99,102,241,0.4)',
      '--ts-glass-bg':       'rgba(99,102,241,0.06)',
      '--ts-glass-border':   'rgba(99,102,241,0.15)',
      '--ts-btn-text':       '#fff',
    },
  },

  /* ─── 5. Gold Rush ─── */
  {
    id: 'gold-rush',
    name: 'Gold Rush',
    emoji: '🏆',
    description: 'Gold shimmer · Trophy cabinet',
    showcaseClass: 'ts-theme-gold',
    vars: {
      '--bg-primary':        '#0C0900',
      '--bg-secondary':      '#1A1300',
      '--bg-card':           '#201800',
      '--bg-card-hover':     '#2A2100',
      '--accent-amber':      '#FFD700',
      '--accent-amber-dark': '#FFC500',
      '--accent-green':      '#FFA500',
      '--accent-red':        '#FF6347',
      '--border':            '#4A3800',
      '--border-subtle':     '#2E2200',
      '--text-primary':      '#FFF8E1',
      '--text-secondary':    '#C9A227',
      '--text-muted':        '#6B5000',
      '--ts-a':              '#FFD700',
      '--ts-b':              '#FFA500',
      '--ts-gradient':       'linear-gradient(135deg,#FFD700 0%,#FFA500 100%)',
      '--ts-glow':           'rgba(255,215,0,0.6)',
      '--ts-glass-bg':       'rgba(255,215,0,0.06)',
      '--ts-glass-border':   'rgba(255,215,0,0.22)',
      '--ts-btn-text':       '#000',
    },
  },
];

export const DEFAULT_THEME_ID = 'sunset-fc';

export function applyTheme(theme: AppTheme): void {
  const root = document.documentElement;
  Object.entries(theme.vars).forEach(([k, v]) => root.style.setProperty(k, v));
  localStorage.setItem('topqik_theme', JSON.stringify(theme.vars));
  localStorage.setItem('topqik_theme_id', theme.id);
}

export function loadSavedTheme(): void {
  const saved = localStorage.getItem('topqik_theme');
  if (!saved) return;
  try {
    const vars = JSON.parse(saved) as Record<string, string>;
    Object.entries(vars).forEach(([k, v]) =>
      document.documentElement.style.setProperty(k, v)
    );
  } catch { /* corrupt — ignore */ }
}
